import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/editarStock.css';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

const REASONS = [
  { value: 'recuento',         label: 'Recuento' },
  { value: 'merma',            label: 'Merma' },
  { value: 'error_ingreso',    label: 'Error de ingreso' },
  { value: 'producto_vencido', label: 'Producto vencido' },
  { value: 'dano',             label: 'Daño' },
  { value: 'cambio_lote',      label: 'Cambio de lote' },
  { value: 'cambio_ubicacion', label: 'Cambio de ubicación' },
  { value: 'correccion_fecha', label: 'Corrección de fecha' },
  { value: 'otro',             label: 'Otro' },
];

function EditarStock() {
  const { token, userRole, logout } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [skus, setSkus] = useState([]);          // list of matching SKUs (Sku.code is not unique)
  const [selectedSkuId, setSelectedSkuId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [editing, setEditing] = useState(null);  // position object being edited
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [toast, setToast] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Token ${token}`,
  };

  const handleBack = () => {
    navigate(userRole === 'storage' ? '/storage' : '/init');
  };

  const handleLogout = () => logout();

  const search = async (e) => {
    if (e) e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setSearching(true);
    setSearchError('');
    setSkus([]);
    setSelectedSkuId(null);
    setEditing(null);
    try {
      const res = await fetch(`${API_BASE}/positions-by-sku/?code=${encodeURIComponent(trimmed)}`, { headers });
      const data = await res.json();
      if (!res.ok) {
        setSearchError(data.error || 'Error al buscar.');
        return;
      }
      setSkus(data);
      if (data.length === 1) setSelectedSkuId(data[0].sku_id);
    } catch (err) {
      setSearchError('Error de conexión.');
    } finally {
      setSearching(false);
    }
  };

  const selectedSku = skus.find((s) => s.sku_id === selectedSkuId) || null;

  const startEdit = (position) => {
    setEditing(position);
    setForm({
      rack: position.rack,
      shelf: position.shelf,
      batch: position.batch || '',
      quantity: position.quantity,
      expiration_date: position.expiration_date || '',
      picking: !!position.picking,
      reason: '',
    });
    setSaveError('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(null);
    setSaveError('');
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildDiff = () => {
    if (!editing || !form) return [];
    const diffs = [];
    if (form.rack !== editing.rack) diffs.push(`Rack: ${editing.rack} → ${form.rack}`);
    if (form.shelf !== editing.shelf) diffs.push(`Shelf: ${editing.shelf} → ${form.shelf}`);
    if (form.batch !== (editing.batch || '')) diffs.push(`Lote: ${editing.batch || '-'} → ${form.batch || '-'}`);
    if (String(form.quantity) !== String(editing.quantity)) diffs.push(`Cantidad: ${editing.quantity} → ${form.quantity}`);
    const oldExp = editing.expiration_date || '';
    if (form.expiration_date !== oldExp) diffs.push(`Vencimiento: ${oldExp || '-'} → ${form.expiration_date || '-'}`);
    if (form.picking !== !!editing.picking) diffs.push(`Picking: ${editing.picking ? 'Sí' : 'No'} → ${form.picking ? 'Sí' : 'No'}`);
    return diffs;
  };

  const save = async () => {
    if (!form.reason) {
      setSaveError('Selecciona un motivo.');
      return;
    }
    const qty = Number(form.quantity);
    if (Number.isNaN(qty) || qty < 0) {
      setSaveError('La cantidad debe ser mayor o igual a 0.');
      return;
    }
    const diffs = buildDiff();
    if (diffs.length === 0) {
      setSaveError('No hay cambios que guardar.');
      return;
    }
    const reasonLabel = REASONS.find((r) => r.value === form.reason)?.label || form.reason;
    const ok = window.confirm(
      `¿Confirmar ajuste?\n\n${diffs.join('\n')}\n\nMotivo: ${reasonLabel}`
    );
    if (!ok) return;

    setSaving(true);
    setSaveError('');
    try {
      const body = {
        position_id: editing.id,
        rack: form.rack,
        shelf: form.shelf,
        // batch is intentionally omitted: the backend re-derives it from the
        // expiration date (the encoding key is server-side only).
        quantity: form.quantity,
        expiration_date: form.expiration_date || null,
        picking: form.picking,
        reason: form.reason,
      };
      const res = await fetch(`${API_BASE}/edit-position/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveError(data.error || 'Error al guardar.');
        return;
      }
      setToast('Posición actualizada.');
      setTimeout(() => setToast(''), 3000);
      cancelEdit();
      // Refetch the position list for the selected SKU.
      await search();
    } catch (err) {
      setSaveError('Error de conexión.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="es-page">
      <button onClick={handleBack} className="es-back-button">&#8592;</button>
      <button className="logout-button" onClick={handleLogout}>Cerrar Sesión</button>

      <div className="es-container">
        <h1 className="es-title">Edición / Ajuste de Stock</h1>

        {!editing && (
          <>
            <form className="es-search" onSubmit={search}>
              <input
                className="es-input"
                type="text"
                placeholder="Código SKU"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
              <button className="es-btn es-btn-primary" type="submit" disabled={searching}>
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </form>

            {searchError && <div className="es-error">{searchError}</div>}

            {skus.length > 1 && (
              <div className="es-sku-picker">
                <p className="es-help">Hay más de un SKU con ese código. Elige uno:</p>
                {skus.map((s) => (
                  <button
                    key={s.sku_id}
                    className={`es-sku-option ${selectedSkuId === s.sku_id ? 'es-sku-option-active' : ''}`}
                    onClick={() => setSelectedSkuId(s.sku_id)}
                    type="button"
                  >
                    <strong>{s.code}</strong> · {s.name} · {s.bar_code}{s.frozen ? ' · ❄' : ''}
                  </button>
                ))}
              </div>
            )}

            {selectedSku && (
              <div className="es-results">
                <div className="es-sku-header">
                  <div><strong>{selectedSku.code}</strong> — {selectedSku.name}</div>
                  <div className="es-sku-sub">Cód. barra: {selectedSku.bar_code}{selectedSku.frozen ? ' · Frío ❄' : ''}</div>
                </div>

                {selectedSku.positions.length === 0 ? (
                  <div className="es-empty">Este SKU no tiene posiciones registradas.</div>
                ) : (
                  <div className="es-position-list">
                    {selectedSku.positions.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="es-position-row"
                        onClick={() => startEdit(p)}
                      >
                        <div className="es-pr-loc">{p.rack}-{p.shelf}</div>
                        <div className="es-pr-line">
                          <span className="es-pr-label">Lote:</span> {p.batch || '-'}
                        </div>
                        <div className="es-pr-line">
                          <span className="es-pr-label">Cantidad:</span> {p.quantity}
                        </div>
                        <div className="es-pr-line">
                          <span className="es-pr-label">Vence:</span> {p.expiration_date || '-'}
                        </div>
                        <div className="es-pr-line">
                          <span className="es-pr-label">Picking:</span> {p.picking ? 'Sí' : 'No'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {editing && form && (
          <div className="es-edit">
            <div className="es-sku-header">
              <div><strong>{selectedSku?.code}</strong> — {selectedSku?.name}</div>
              <div className="es-sku-sub">Posición original: {editing.rack}-{editing.shelf} · Lote {editing.batch || '-'}</div>
            </div>

            <div className="es-form-grid">
              <label className="es-field">
                <span>Rack</span>
                <input className="es-input" type="text" value={form.rack} onChange={(e) => updateForm('rack', e.target.value)} />
              </label>
              <label className="es-field">
                <span>Shelf</span>
                <input className="es-input" type="text" value={form.shelf} onChange={(e) => updateForm('shelf', e.target.value)} />
              </label>
              <label className="es-field">
                <span>Lote</span>
                <input className="es-input" type="text" value={form.batch} readOnly disabled />
                <small className="es-hint">Se genera automáticamente desde la fecha de vencimiento.</small>
              </label>
              <label className="es-field">
                <span>Cantidad</span>
                <input
                  className="es-input"
                  type="number"
                  step="0.001"
                  min="0"
                  value={form.quantity}
                  onChange={(e) => updateForm('quantity', e.target.value)}
                />
              </label>
              <label className="es-field">
                <span>Fecha de vencimiento</span>
                <input
                  className="es-input"
                  type="date"
                  value={form.expiration_date || ''}
                  onChange={(e) => updateForm('expiration_date', e.target.value)}
                />
              </label>
              <label className="es-field es-field-checkbox">
                <input
                  type="checkbox"
                  checked={form.picking}
                  onChange={(e) => updateForm('picking', e.target.checked)}
                />
                <span>Posición de picking</span>
              </label>
              <label className="es-field es-field-full">
                <span>Motivo</span>
                <select className="es-input" value={form.reason} onChange={(e) => updateForm('reason', e.target.value)}>
                  <option value="">-- Selecciona un motivo --</option>
                  {REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
            </div>

            {saveError && <div className="es-error">{saveError}</div>}

            <div className="es-actions">
              <button className="es-btn es-btn-secondary" onClick={cancelEdit} disabled={saving}>
                Cancelar
              </button>
              <button className="es-btn es-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        {toast && <div className="es-toast">{toast}</div>}
      </div>
    </div>
  );
}

export default EditarStock;
